// src/pages/Login.jsx
import { useState, useEffect } from "react";
import { Eye, EyeOff, KeyRound, Lock, Mail, X, Loader2 } from "lucide-react";
import whitelogo from "../assets/whiteintel-white-logo-new.png";
import { getSafeNext } from "../utils/auth";

export default function Login() {
  // API endpoints
  const LOGIN_URL = "https://whiteintel.io/api/regular/app/login_handler_v5.php";
  const MFA_VERIFY_URL = "https://whiteintel.io/api/regular/app/twofactor_handler.php";
  const MFA_RESEND_URL = "https://whiteintel.io/api/regular/app/twofactor_resend_handler.php";
  const AZURE_CODE_EXCHANGE_URL = "https://whiteintel.io/api/regular/app/azure_callback_sso.php";
  const AZURE_REDIRECT_URI = "https://platform.whiteintel.io/login";
  const ACTIVATE_URL = "https://whiteintel.io/api/regular/pub/activate.php";

  // UI state@
  const [step, setStep] = useState("login"); // "login" | "mfa"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [isActivateLoading, setIsActivateLoading] = useState("");

  // MFA state
  const [challengeId, setChallengeId] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [useBackup] = useState(false);

  // Resend state
  const [resendLoading, setResendLoading] = useState(false);
  const [resendLeft, setResendLeft] = useState(0); // seconds

  // Cooldown timer
  useEffect(() => {
    if (resendLeft <= 0) return;
    const t = setInterval(() => setResendLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendLeft]);

  /* ---------- 1) Handle Azure OAuth callback (code/state) ---------- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const oauthError = params.get("error");
    const state = params.get("state");

    if (oauthError) {
      setErr(`Microsoft sign-in failed: ${oauthError}`);
      const url = new URL(window.location.href);
      url.searchParams.delete("code");
      url.searchParams.delete("state");
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : ""));
      return;
    }

    if (!code) return;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await fetch(AZURE_CODE_EXCHANGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, state, redirect_uri: AZURE_REDIRECT_URI }),
        });

        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const j = await res.json();
            if (j?.error) msg = j.error;
          } catch {}
          throw new Error(msg);
        }

        const json = await res.json();

        if (json?.status === "success" && json?.token) {
          localStorage.setItem("token", json.token);
          // scrub params then redirect home
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : ""));
          window.location.href = getSafeNext("/");
          return;
        }

        if (json?.twofa === "required" && json?.temp_token) {
          setChallengeId(json.temp_token);
          setStep("mfa");
          const url = new URL(window.location.href);
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          window.history.replaceState({}, "", url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : ""));
          return;
        }

        throw new Error(json?.error || "Microsoft sign-in verification failed");
      } catch (e) {
        setErr(e?.message || "Microsoft sign-in verification failed");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ---------- 2) Handle activation link (?activateToken=...) ---------- */
  useEffect(() => {
    const url = new URL(window.location.href);
    const activateToken = url.searchParams.get("activateToken");
    if (!activateToken) return;

    setIsActivateLoading(true);

    (async () => {
      try {
        setErr("");
        const res = await fetch(ACTIVATE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: activateToken }),
        });

        const json = await res.json().catch(() => ({}));
        if (!res.ok || json?.success === false) {
          throw new Error(json?.error || `Activation failed (HTTP ${res.status})`);
        }

        setNotice(json?.message || "Your account has been activated. You can now sign in.");
      } catch (e) {
        setErr(e?.message || "Activation failed");
        setIsActivateLoading(false);

      } finally {
        url.searchParams.delete("activateToken");
        const qs = url.searchParams.toString();
        window.history.replaceState({}, "", url.pathname + (qs ? `?${qs}` : ""));
        setLoading(false);
        setIsActivateLoading(false);
      }
    })();
  }, []);

  /* ---------- Login / MFA handlers ---------- */
  const onLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);

    try {
      const res = await fetch(LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: String(email).trim(), password }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || res.statusText || "Authentication failed");

      // SSO redirect case
      if (json?.sso === true && json?.authorize_url) {
        window.location.href = json.authorize_url;
        return;
      }

      // Fully authenticated
      if (json?.status === "success" && json?.token) {
        localStorage.setItem("token", json.token);
        window.location.href = getSafeNext("/global-stats");
        return;
      }

      // 2FA required
      if (json?.twofa === "required" && json?.temp_token) {
        setChallengeId(json.temp_token);
        setStep("mfa");
        return;
      }

      throw new Error(json?.error || "Authentication failed");
    } catch (e) {
      setErr(e.message || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const onVerifyMfa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await fetch(MFA_VERIFY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_token: challengeId,
          twofactor: mfaCode.trim(),
        }),
      });

      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }

      const json = await res.json();

      if (json?.status === "success" && json?.token) {
        localStorage.setItem("token", json.token);
        window.location.href = getSafeNext("/");
        return;
      }
      throw new Error(json?.error || "MFA verification failed");
    } catch (e) {
      setErr(e.message || "MFA verification failed");
    } finally {
      setLoading(false);
    }
  };

  const onResendMfa = async () => {
    if (!challengeId) {
      setErr("Missing MFA challenge. Please go back and sign in again.");
      return;
    }
    if (resendLeft > 0 || resendLoading) return;

    setErr("");
    setNotice("");
    setResendLoading(true);
    try {
      const res = await fetch(MFA_RESEND_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ temp_token: challengeId }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || `Resend failed (HTTP ${res.status})`);
      }

      setNotice(json?.message || "A new verification code has been sent.");
      setResendLeft(30); // cooldown (seconds)
    } catch (e) {
      setErr(e?.message || "Could not resend code");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white grid grid-cols-1 md:grid-cols-2">
      {/* Left brand panel (desktop only) */}
      <aside className="relative hidden md:flex flex-col border-r border-neutral-800 p-10
                        bg-neutral-900/40 shadow-[0_0_30px_rgba(56,189,248,0.06)] backdrop-blur overflow-hidden">

        {/* --- Background layers (subtle, performant) --- */}
        {/* Dots pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml;utf8,
              <svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'>
                <circle cx='1.25' cy='1.25' r='1.25' fill='%23a3b3c5' fill-opacity='0.35'/>
              </svg>")`,
            backgroundSize: "20px 20px"
          }}
        />

        {/* Soft cyan/violet corner glow */}
        <div
          className="pointer-events-none absolute -right-40 -top-32 h-80 w-80 rounded-full blur-3xl opacity-30"
          style={{ background: "linear-gradient(180deg, rgba(56,189,248,0.25), rgba(99,102,241,0.15))" }}
        />

        {/* Gentle vignette from top-left */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(900px 450px at 10% 0%, rgba(56,189,248,0.10) 0%, rgba(0,0,0,0) 60%)"
          }}
        />

        {/* --- Content --- */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-start pt-[24vh] text-center">
          <img
            src={whitelogo}
            alt="WhiteIntel"
            className="h-48 w-48 md:h-48 md:w-48 rounded-2xl"
          />
          <div
            className="mt-4 text-3xl font-bold leading-tight tracking-wide"
            style={{ fontFamily: '"Outfit", ui-sans-serif, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
          >
            Whiteintel Dark Web Intelligence Services
          </div>

          <p className="mt-3 max-w-sm text-sm text-neutral-400 leading-relaxed">
            Real-time information stealer and credential leak intelligence for Security Teams, MSSPs, Enterprises, and Researchers.
          </p>
        </div>

        {/* Bottom links */}
        <div className="relative z-10 absolute inset-x-0 bottom-6">
          <div className="mx-auto flex flex-col items-center justify-center gap-5 text-xs text-neutral-500">
            {/* Links */}
            <div className="flex items-center justify-center gap-4">
              <a href="https://whiteintel.io" className="underline" target="_blank">Home</a>
              <a href="https://knowledge.whiteintel.io/" className="underline" target="_blank">Knowledge Center</a>
              <a href="https://whiteintel.io/terms" className="underline" target="_blank">Terms</a>
              <a href="https://whiteintel.io/privacy" className="underline" target="_blank">Privacy</a>
            </div>

            {/* Version number */}
            <div className="text-[12px] text-neutral-600">
              v4.0.3 © 2025 Whiteintel
            </div>
          </div>
        </div>
      </aside>

      {/* Right: form panel (mobile keeps single column) */}
      <main className="flex items-center justify-center px-6 md:px-10 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 shadow-[0_0_30px_rgba(56,189,248,0.06)] backdrop-blur">
            <div className="px-8 pt-8 pb-6">
              {/* Small brand row for mobile */}
              <div className="md:hidden flex items-center gap-2 text-large text-neutral-400">
                <img src={whitelogo} alt="WhiteIntel" className="h-8 w-8" />
                <span>WhiteIntel</span>
              </div>

              <h2 className="mt-1 text-2xl font-semibold">Welcome back</h2>
              <p className="text-sm text-gray-400">Leave the password empty for Microsoft SSO.</p>

    {isActivateLoading && (
                <div className="mt-6">
                  {/* Text Label */}
                  <div className="mb-2 flex items-center justify-between text-xs font-medium uppercase tracking-widest text-sky-400">
                    <span>Verifying the Account</span>
                    <span className="animate-pulse">...</span>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="h-1 w-full overflow-hidden rounded-full bg-neutral-800">
                    <div className="h-full w-full origin-left animate-[scale-x_1s_ease-in-out_infinite] bg-sky-500/60" />
                  </div>
                </div>
              )}

{err && (
  <div
    className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300"
    role="alert"
  >
    <div className="flex items-start justify-between gap-3">
      <span className="pt-0.5">{err}</span>
      <button
        type="button"
        onClick={() => setErr("")}
        className="shrink-0 p-1 rounded hover:bg-red-500/20 text-red-300"
        aria-label="Dismiss error"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>
)}

{notice && (
  <div className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300">
    <div className="flex items-start justify-between gap-3">
      <span className="pt-0.5">{notice}</span>
      <button
        type="button"
        onClick={() => setNotice("")}
        className="shrink-0 p-1 rounded hover:bg-emerald-500/20 text-emerald-300"
        aria-label="Dismiss notice"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  </div>
)}

              {/* Forms (no SSO button; empty password triggers SSO) */}
              {step === "login" && (
                <form onSubmit={onLogin} className="mt-6 space-y-4" noValidate>
                  <div>
                    <label className="mb-1 block text-sm text-neutral-300" htmlFor="email">Email</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <Mail className="h-4 w-4 text-neutral-400" aria-hidden />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                        autoComplete="username"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300" htmlFor="password">Password</label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <Lock className="h-4 w-4 text-neutral-400" aria-hidden />
                      <input
                        id="password"
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px]"
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((s) => !s)}
                        className="p-1 text-neutral-400 hover:text-neutral-200"
                        aria-label={showPw ? "Hide password" : "Show password"}
                      >
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <a href="/recover" className="text-sm text-sky-300 hover:text-sky-200">Forgot password?</a>
                    <a href="/signup" className="text-sm text-sky-300 hover:text-sky-200">Register for free</a>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || isActivateLoading}
                    className="mt-2 w-full rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2.5 text-[15px] font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
                  >
                    {loading ? "Signing in…" : "Sign in"}
                  </button>
                </form>
              )}

              {step === "mfa" && (
                <form onSubmit={onVerifyMfa} className="mt-6 space-y-4">
                  <div className="rounded-lg border border-neutral-800 bg-neutral-900/20 px-3 py-2 text-sm text-neutral-300">
                    Check your email and enter <b>6-digit code</b>.
                  </div>

                  <div>
                    <label className="mb-1 block text-sm text-neutral-300">
                      MFA code
                    </label>
                    <div className="flex items-center gap-2 rounded-lg border border-neutral-800 bg-[#000414] px-3 py-2.5 focus-within:border-sky-500/50">
                      <KeyRound className="h-4 w-4 text-neutral-400" aria-hidden />
                      <input
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        placeholder="XYZ6KT"
                        className="w-full bg-transparent outline-none placeholder:text-neutral-500 text-[15px] tracking-widest"
                        maxLength={8}
                        required
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-neutral-500" />
                    <button
                      type="button"
                      onClick={onResendMfa}
                      disabled={resendLoading || resendLeft > 0}
                      className="text-sm text-sky-300 hover:text-sky-200 disabled:opacity-50"
                    >
                      {resendLoading
                        ? "Resending…"
                        : resendLeft > 0
                        ? `Resend in ${resendLeft}s`
                        : "Resend code"}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2.5 text-[15px] font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-50"
                  >
                    {loading ? "Verifying…" : "Verify & Continue"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep("login");
                      setPassword("");
                      setMfaCode("");
                      setErr("");
                      setNotice("");
                    }}
                    className="w-full text-center text-sm text-neutral-400 hover:text-neutral-200 mt-2"
                  >
                    Not you? Sign in with a different account
                  </button>
                </form>
              )}
            </div>

            <div className="rounded-b-2xl border-t border-neutral-800 bg-[#0a1224]/60 px-8 py-4 text-[13px] text-neutral-400">
              By signing in you agree to our{" "}
              <a href="https://whiteintel.io/terms" target="_blank" rel="noreferrer" className="text-sky-300 hover:text-sky-200">Terms</a>
              {" "}and{" "}
              <a href="https://whiteintel.io/privacy" target="_blank" rel="noreferrer" className="text-sky-300 hover:text-sky-200">Privacy Policy</a>.
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
